"use client";

import { useEffect, useState } from "react";
import { useUser } from "@/components/UserContext";
import { useLanguage } from "@/components/LanguageContext";
import { Cookie } from "lucide-react";
import { brandConfig } from "@/lib/brandConfig";

const COOKIE_BANNER_DONE_KEY = "cookieBannerDone";
const COOKIE_CONSENT_KEY = "cookieConsent";
const VISITOR_ARRIVAL_KEY = "visitorArrival";
const GUEST_NAME_KEY = "guestName";
const RECEPTIONIST_COOKIE_BANNER_DONE_KEY = "receptionistCookieBannerDone";

const IS_DEVELOPMENT =
    process.env.NODE_ENV ===
    "development";

const translations = {
    hu: {
        openSettingsAria:
            "Süti beállítások megnyitása",

        openSettingsTitle:
            "Süti beállítások",

        settingsTitle:
            "süti beállítások",

        bannerTitle:
            "sütiket használunk",

        settingsDescription:
            "válaszd ki, mely sütik használatát engedélyezed.",

        bannerDescription:
            "a szükséges sütik mindig aktívak. az analitikai és marketing sütikről te dönthetsz.",

        identifier:
            "Azonosító",

        preparingArrival:
            "érkezési adatok előkészítése...",

        necessary:
            "Szükséges",

        necessaryDescription:
            "Az oldal alapvető működéséhez.",

        analytics:
            "Analitika",

        analyticsDescription:
            "Segít megérteni az oldal használatát.",

        marketing:
            "Marketing",

        marketingDescription:
            "Hirdetések és kampánymérés.",

        necessaryCookies:
            "Szükséges sütik",

        necessaryCookiesDescription:
            "Az oldal működéséhez.",

        analyticsCookies:
            "Analitikai sütik",

        analyticsCookiesDescription:
            "Statisztika és fejlesztés.",

        marketingCookies:
            "Marketing sütik",

        marketingCookiesDescription:
            "Hirdetések és kampánymérés.",

        developmentTracking:
            "Fejlesztői mérés",

        developmentTrackingDescription:
            "Analytics, Meta és TikTok Pixel dev módban.",

        customize:
            "Testreszabás",

        acceptAll:
            "Mindet elfogadom",

        saving:
            "Mentés...",

        back:
            "Vissza",

        saveSettings:
            "Beállítások mentése",

        active:
            "aktív",
    },

    en: {
        openSettingsAria:
            "Open cookie settings",

        openSettingsTitle:
            "Cookie settings",

        settingsTitle:
            "cookie settings",

        bannerTitle:
            "we use cookies",

        settingsDescription:
            "choose which cookies you would like to allow.",

        bannerDescription:
            "necessary cookies are always active. you can choose whether to allow analytics and marketing cookies.",

        identifier:
            "Identifier",

        preparingArrival:
            "preparing visit data...",

        necessary:
            "Necessary",

        necessaryDescription:
            "Required for the website to work properly.",

        analytics:
            "Analytics",

        analyticsDescription:
            "Helps us understand how the website is used.",

        marketing:
            "Marketing",

        marketingDescription:
            "Advertising and campaign measurement.",

        necessaryCookies:
            "Necessary cookies",

        necessaryCookiesDescription:
            "Required for the website to function.",

        analyticsCookies:
            "Analytics cookies",

        analyticsCookiesDescription:
            "Statistics and website improvements.",

        marketingCookies:
            "Marketing cookies",

        marketingCookiesDescription:
            "Advertising and campaign measurement.",

        developmentTracking:
            "Development tracking",

        developmentTrackingDescription:
            "Analytics, Meta and TikTok Pixel in development mode.",

        customize:
            "Customize",

        acceptAll:
            "Accept all",

        saving:
            "Saving...",

        back:
            "Back",

        saveSettings:
            "Save settings",

        active:
            "active",
    },
};

function getSavedVisitorArrival() {
    try {
        const savedVisitorArrival =
            window.sessionStorage.getItem(VISITOR_ARRIVAL_KEY);

        if (!savedVisitorArrival) {
            return null;
        }

        return JSON.parse(savedVisitorArrival);
    } catch (error) {
        return null;
    }
}

async function sendGuestArrivalToBackend(nextConsent) {
    const visitorArrival = getSavedVisitorArrival();

    const response = await fetch("/api/guest/arrival", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            consent: nextConsent,
            visitorArrival,
        }),
    });

    const data = await response.json().catch(() => null);

    if (!response.ok || data?.ok !== true) {
        throw new Error("Guest arrival could not be saved.");
    }

    return data;
}

export default function CookieBanner() {
    const brandStyle = {
        "--brand-primary":
            brandConfig.colors.primary,

        "--brand-primary-hover":
            brandConfig.colors.primaryHover,

        "--brand-background":
            brandConfig.colors.background,

        "--brand-text-on-primary":
            brandConfig.colors.textOnPrimary,
    };

    const {
        user,
        isUserLoading,
    } = useUser();

    const {
        language,
    } = useLanguage();

    const t =
        translations[
        language === "en"
            ? "en"
            : "hu"
        ];

    const [isMounted, setIsMounted] = useState(false);
    const [isBannerOpen, setIsBannerOpen] = useState(true);
    const [isCustomizeOpen, setIsCustomizeOpen] = useState(false);
    const [isVisitorArrivalReady, setIsVisitorArrivalReady] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [guestName, setGuestName] = useState("");

    const [consent, setConsent] = useState({
        necessary: true,
        analytics: false,
        marketing: false,
        developmentTracking: false,
    });

    const isReceptionist =
        user?.isReceptionist === true;

    useEffect(() => {
        if (!isMounted) return;
        document.documentElement.dataset.strikeCookieBanner = isBannerOpen ? "open" : "closed";
        window.dispatchEvent(new CustomEvent("strike-cookie-banner-state", { detail: { open: isBannerOpen } }));
    }, [isMounted, isBannerOpen]);

    useEffect(() => {
        if (
            !isMounted ||
            !isBannerOpen
        ) {
            return;
        }

        const previousOverflow =
            document.body.style
                .overflow;

        document.body.style.overflow =
            "hidden";

        return () => {
            document.body.style.overflow =
                previousOverflow;
        };
    }, [
        isMounted,
        isBannerOpen,
    ]);

    useEffect(() => {
        if (isUserLoading) {
            return;
        }

        try {
            const bannerDone =
                window.sessionStorage.getItem(
                    COOKIE_BANNER_DONE_KEY
                );

            const receptionistBannerDone =
                window.localStorage.getItem(
                    RECEPTIONIST_COOKIE_BANNER_DONE_KEY
                );

            const savedConsent =
                window.sessionStorage.getItem(
                    COOKIE_CONSENT_KEY
                );

            const savedGuestName =
                window.sessionStorage.getItem(
                    GUEST_NAME_KEY
                );

            if (savedConsent) {
                setConsent(
                    JSON.parse(savedConsent)
                );
            }

            if (savedGuestName) {
                setGuestName(
                    savedGuestName
                );
            }

            if (
                isReceptionist &&
                receptionistBannerDone === "true"
            ) {
                setIsBannerOpen(false);
            } else {
                setIsBannerOpen(
                    bannerDone !== "true"
                );
            }
        } catch (error) {
            setIsBannerOpen(true);
        }

        setIsMounted(true);
    }, [
        isUserLoading,
        isReceptionist,
    ]);

    useEffect(() => {
        if (!isMounted || !isBannerOpen) {
            return;
        }

        const checkVisitorArrival = () => {
            const visitorArrival = getSavedVisitorArrival();

            if (visitorArrival) {
                setIsVisitorArrivalReady(true);
                return true;
            }

            setIsVisitorArrivalReady(false);
            return false;
        };

        if (checkVisitorArrival()) {
            return;
        }

        const intervalId = window.setInterval(() => {
            if (checkVisitorArrival()) {
                window.clearInterval(intervalId);
            }
        }, 100);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [isMounted, isBannerOpen]);

    const isActionDisabled = isSaving || !isVisitorArrivalReady;

    const saveConsent = async (nextConsent) => {
        if (!isVisitorArrivalReady || isSaving) {
            return;
        }

        setIsSaving(true);

        try {
            try {
                window.sessionStorage.setItem(
                    COOKIE_CONSENT_KEY,
                    JSON.stringify(
                        nextConsent
                    )
                );
            } catch (error) {
                console.log(
                    "A böngésző storage nem elérhető."
                );
            }

            window.dispatchEvent(
                new CustomEvent(
                    "cookie-consent-saved",
                    {
                        detail: {
                            consent:
                                nextConsent,
                        },
                    }
                )
            );

            const data =
                await sendGuestArrivalToBackend(
                    nextConsent
                );

            if (data?.guestname) {
                setGuestName(
                    data.guestname
                );

                try {
                    window.sessionStorage.setItem(
                        GUEST_NAME_KEY,
                        data.guestname
                    );
                } catch (error) {
                    console.log(
                        "A guestName nem menthető sessionStorage-ba."
                    );
                }
            }

            try {
                window.sessionStorage.setItem(
                    COOKIE_BANNER_DONE_KEY,
                    "true"
                );

                if (isReceptionist) {
                    window.localStorage.setItem(
                        RECEPTIONIST_COOKIE_BANNER_DONE_KEY,
                        "true"
                    );
                }
            } catch (error) {
                console.log(
                    "A böngésző storage nem elérhető."
                );
            }

            setConsent(
                nextConsent
            );

            setIsBannerOpen(
                false
            );

            setIsCustomizeOpen(
                false
            );
        } catch (error) {
            // console.log(
            //     "Guest arrival could not be sent to backend."
            // );
        } finally {
            setIsSaving(
                false
            );
        }
    };

    const handleAcceptAll = () => {
        saveConsent({
            necessary: true,
            analytics: true,
            marketing: true,
            developmentTracking:
                IS_DEVELOPMENT
                    ? consent.developmentTracking
                    : false,
        });
    };

    const handleSaveCustom = () => {
        saveConsent({
            necessary: true,
            analytics:
                consent.analytics,

            marketing:
                consent.marketing,

            developmentTracking:
                IS_DEVELOPMENT
                    ? consent.developmentTracking
                    : false,
        });
    };

    const handleRejectOptional = () => {
        saveConsent({
            necessary: true,
            analytics: false,
            marketing: false,
            developmentTracking: false,
        });
    };

    const handleCustomize = () => {
        if (isActionDisabled) {
            return;
        }

        setConsent({
            necessary: true,
            analytics: true,
            marketing: true,
            developmentTracking: false,
        });

        setIsCustomizeOpen(true);
    };

    const handleBack = () => {
        if (isSaving) {
            return;
        }

        setIsCustomizeOpen(false);
    };

    const handleOpenBanner = () => {
        setIsBannerOpen(true);
    };

    const toggleConsent = (key) => {
        if (isSaving) {
            return;
        }

        setConsent((currentConsent) => ({
            ...currentConsent,
            [key]: !currentConsent[key],
        }));
    };

    if (!isMounted) {
        return null;
    }

    if (!isBannerOpen) {
        return (
            <button
                type="button"
                style={brandStyle}
                onClick={handleOpenBanner}
                aria-label={
                    t.openSettingsAria
                }
                title={
                    t.openSettingsTitle
                }
                className={`
                fixed bottom-4 left-4 z-[99900]
                h-[54px] w-[54px]
                cursor-pointer
                items-center justify-center
                rounded-full
                border-2 border-[var(--brand-primary)]
                bg-black text-[var(--brand-primary)]
                shadow-[0_12px_35px_rgba(0,0,0,0.55)]
                transition duration-200
                hover:-translate-y-1
                hover:bg-[var(--brand-primary)]
                hover:text-[var(--brand-text-on-primary)]
                active:scale-95
                sm:bottom-6 sm:left-6
                ${IS_DEVELOPMENT
                        ? "hidden md:flex"
                        : "flex"
                    }
            `}
            >
                <Cookie
                    size={27}
                    strokeWidth={2.5}
                />
            </button>
        );
    }

    return (
        <div
            style={brandStyle}
            className="
                fixed inset-0
                z-[999999]
                flex items-end
                bg-black/70
                backdrop-blur-sm
                md:items-center
                md:justify-center
                md:p-6
            "
        >
            <div
                className="
                    relative
                    w-full
                    border-t-2
                    border-[var(--brand-primary)]
                    bg-[var(--brand-background)]
                    px-5
                    pb-[calc(24px+env(safe-area-inset-bottom))]
                    pt-5
                    text-white
                    shadow-[0_-25px_80px_rgba(0,0,0,0.75)]
                    md:max-w-[440px]
                    md:border-2
                    md:border-[var(--brand-primary)]
                    md:px-7
                    md:pb-7
                    md:pt-7
                    md:[box-shadow:12px_12px_0_0_var(--brand-primary)]
                "
            >
                <div
                    className="
                        flex
                        items-center
                        gap-4
                    "
                >
                    <div
                        className="
                            flex h-12 w-12
                            shrink-0
                            items-center
                            justify-center
                            rounded-full
                            bg-[var(--brand-primary)]
                            text-[var(--brand-text-on-primary)]
                        "
                    >
                        <Cookie
                            size={23}
                            strokeWidth={2.5}
                        />
                    </div>

                    <div className="min-w-0">
                        <p
                            className="
                                text-[10px]
                                font-black
                                uppercase
                                tracking-[0.16em]
                                text-white/60
                            "
                        >
                            {brandConfig.name}
                        </p>

                        <h2
                            className="
                                mt-1
                                text-[19px]
                                font-black
                                lowercase
                                text-white
                                md:text-[22px]
                            "
                        >
                            {isCustomizeOpen
                                ? t.settingsTitle
                                : t.bannerTitle}
                        </h2>
                    </div>
                </div>

                <p
                    className="
                        mt-5
                        max-w-[350px]
                        text-[13px]
                        leading-[1.45]
                        text-white/70
                        md:text-[14px]
                    "
                >
                    {isCustomizeOpen
                        ? t.settingsDescription
                        : t.bannerDescription}
                </p>

                <div
                    className="
                        mt-5
                        border-t
                        border-white/10
                        pt-4
                    "
                >
                    {!isCustomizeOpen ? (
                        <>
                            {guestName && (
                                <p className="mt-3 text-[11px] font-medium text-white/60">
                                    {t.identifier}:{" "}
                                    <span className="text-white/70">
                                        {guestName}
                                    </span>
                                </p>
                            )}

                            {!isVisitorArrivalReady && (
                                <div className="mt-4 flex items-center gap-2 rounded-xl bg-[color-mix(in_srgb,var(--brand-primary)_10%,transparent)] px-3 py-2.5 text-xs font-bold text-[var(--brand-primary)]">
                                    <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--brand-primary)]" />

                                    {t.preparingArrival}
                                </div>
                            )}

                            <div className="mt-5 space-y-2">
                                <CookieInfoRow
                                    title={
                                        t.necessary
                                    }
                                    description={
                                        t.necessaryDescription
                                    }
                                    active
                                    activeLabel={
                                        t.active
                                    }
                                />

                                <CookieInfoRow
                                    title={
                                        t.analytics
                                    }
                                    description={
                                        t.analyticsDescription
                                    }
                                    activeLabel={
                                        t.active
                                    }
                                />

                                <CookieInfoRow
                                    title={
                                        t.marketing
                                    }
                                    description={
                                        t.marketingDescription
                                    }
                                    activeLabel={
                                        t.active
                                    }
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="space-y-2">
                                <CookieToggleRow
                                    title={
                                        t.necessaryCookies
                                    }
                                    description={
                                        t.necessaryCookiesDescription
                                    }
                                    checked
                                    disabled
                                />

                                <CookieToggleRow
                                    title={
                                        t.analyticsCookies
                                    }
                                    description={
                                        t.analyticsCookiesDescription
                                    }
                                    checked={
                                        consent.analytics
                                    }
                                    onChange={() => {
                                        toggleConsent(
                                            "analytics"
                                        );
                                    }}
                                    disabled={
                                        isSaving
                                    }
                                />

                                <CookieToggleRow
                                    title={
                                        t.marketingCookies
                                    }
                                    description={
                                        t.marketingCookiesDescription
                                    }
                                    checked={
                                        consent.marketing
                                    }
                                    onChange={() => {
                                        toggleConsent(
                                            "marketing"
                                        );
                                    }}
                                    disabled={
                                        isSaving
                                    }
                                />

                                {IS_DEVELOPMENT && (
                                    <CookieToggleRow
                                        title={
                                            t.developmentTracking
                                        }
                                        description={
                                            t.developmentTrackingDescription
                                        }
                                        checked={
                                            consent
                                                .developmentTracking ===
                                            true
                                        }
                                        onChange={() => {
                                            toggleConsent(
                                                "developmentTracking"
                                            );
                                        }}
                                        disabled={
                                            isSaving
                                        }
                                    />
                                )}
                            </div>

                            {guestName && (
                                <p className="mt-4 text-[11px] text-white/60">
                                    {t.identifier}:{" "}
                                    <span className="text-white/70">
                                        {guestName}
                                    </span>
                                </p>
                            )}
                        </>
                    )}
                </div>

                <div
                    className="
                        mt-4
                        border-t
                        border-white/10
                        pt-4
                    "
                >
                    {!isCustomizeOpen ? (
                        <div className="grid grid-cols-2 gap-2">
                            <button
                                type="button"
                                onClick={
                                    handleCustomize
                                }
                                disabled={
                                    isActionDisabled
                                }
                                className="
                                    min-h-[46px]
                                    cursor-pointer
                                    rounded-xl border
                                    border-white/10
                                    bg-white/[0.03]
                                    px-4 text-sm
                                    font-bold text-white/70
                                    transition
                                    hover:border-[var(--brand-primary)]
                                    hover:text-white
                                    active:scale-[0.98]
                                    disabled:cursor-not-allowed
                                    disabled:opacity-50
                                "
                            >
                                {t.customize}
                            </button>

                            <button
                                type="button"
                                onClick={
                                    handleAcceptAll
                                }
                                disabled={
                                    isActionDisabled
                                }
                                className="
                                    min-h-[46px]
                                    cursor-pointer
                                    rounded-xl
                                    bg-[var(--brand-primary)]
                                    px-4 text-sm
                                    font-black
                                    text-[var(--brand-text-on-primary)]
                                    transition
                                    hover:bg-[var(--brand-primary-hover)]
                                    active:scale-[0.98]
                                    disabled:cursor-not-allowed
                                    disabled:opacity-50
                                "
                            >
                                {isSaving
                                    ? t.saving
                                    : t.acceptAll}
                            </button>
                        </div>
                    ) : (
                        <>
                            <button
                                type="button"
                                onClick={
                                    handleAcceptAll
                                }
                                disabled={
                                    isActionDisabled
                                }
                                className="
                                    min-h-[46px]
                                    w-full
                                    cursor-pointer
                                    rounded-xl
                                    bg-[var(--brand-primary)]
                                    px-4 text-sm
                                    font-black
                                    text-[var(--brand-text-on-primary)]
                                    transition
                                    hover:bg-[var(--brand-primary-hover)]
                                    active:scale-[0.98]
                                    disabled:cursor-not-allowed
                                    disabled:opacity-50
                                "
                            >
                                {isSaving
                                    ? t.saving
                                    : t.acceptAll}
                            </button>

                            <div className="mt-2 grid grid-cols-2 gap-2">
                                <button
                                    type="button"
                                    onClick={
                                        handleBack
                                    }
                                    disabled={
                                        isSaving
                                    }
                                    className="
                                        min-h-[44px]
                                        cursor-pointer
                                        rounded-xl border
                                        border-white/10
                                        bg-white/[0.03]
                                        px-4 text-sm
                                        font-bold text-white/70
                                        transition
                                        hover:border-[var(--brand-primary)]
                                        hover:text-white
                                        active:scale-[0.98]
                                        disabled:cursor-not-allowed
                                        disabled:opacity-50
                                    "
                                >
                                    {t.back}
                                </button>

                                <button
                                    type="button"
                                    onClick={
                                        handleSaveCustom
                                    }
                                    disabled={
                                        isActionDisabled
                                    }
                                    className="
                                        min-h-[44px]
                                        cursor-pointer
                                        rounded-xl border
                                        border-white/10
                                        bg-white/[0.03]
                                        px-4 text-sm
                                        font-bold text-white/70
                                        transition
                                        hover:border-[var(--brand-primary)]
                                        hover:text-white
                                        active:scale-[0.98]
                                        disabled:cursor-not-allowed
                                        disabled:opacity-50
                                    "
                                >
                                    {isSaving
                                        ? t.saving
                                        : t.saveSettings}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}

function CookieInfoRow({
    title,
    description,
    active = false,
    activeLabel = "aktív",
}) {
    return (
        <div className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.025] px-3.5 py-3">
            <span
                className={`
                    h-2.5 w-2.5 shrink-0
                    rounded-full
                    ${active
                        ? "bg-[var(--brand-primary)]"
                        : "bg-white/20"
                    }
                `}
            />

            <div className="min-w-0">
                <p className="text-sm font-bold text-white/85">
                    {title}
                </p>

                <p className="mt-0.5 text-xs text-white/65">
                    {description}
                </p>
            </div>

            {active && (
                <span className="ml-auto shrink-0 text-[10px] font-bold text-[var(--brand-primary)]">
                    {activeLabel}
                </span>
            )}
        </div>
    );
}

function CookieToggleRow({
    title,
    description,
    checked,
    onChange,
    disabled,
}) {
    return (
        <button
            type="button"
            onClick={
                disabled
                    ? undefined
                    : onChange
            }
            disabled={
                disabled
            }
            className={`
                flex w-full items-center gap-3
                rounded-xl border border-white/[0.07]
                bg-white/[0.025]
                px-3.5 py-3 text-left
                transition
                ${disabled
                    ? "cursor-not-allowed opacity-60"
                    : "cursor-pointer hover:bg-white/[0.05]"
                }
            `}
        >
            <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-white/85">
                    {title}
                </p>

                <p className="mt-0.5 text-xs text-white/65">
                    {description}
                </p>
            </div>

            <span
                className={`
                    relative h-6 w-11
                    shrink-0 rounded-full
                    transition
                    ${checked
                        ? "bg-[var(--brand-primary)]"
                        : "bg-white/10"
                    }
                `}
            >
                <span
                    className={`
                        absolute top-[3px]
                        h-[18px] w-[18px]
                        rounded-full
                        bg-[var(--brand-text-on-primary)]
                        transition-all
                        ${checked
                            ? "left-[23px]"
                            : "left-[3px]"
                        }
                    `}
                />
            </span>
        </button>
    );
}